import React, { useEffect, useState } from "react";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DataPickerButton from "react-multi-date-picker/components/button";
import DateObject from "react-date-object";
import {
  formatChartDateDaily,
  formatChartDateWeekly,
  formatChartDateCustom,
  formatChartDateMonthly,
  formatChartDateYearly,
} from "./ChartsDateAndTimeFunctions";

const ChartsDateInputs = ({
  device,
  setParameter,
  setSelectedStartDate,
  setSelectedEndDate,
  selectedStartDate,
  selectedEndDate,
  dataType,
  setChartType,
  setDataType,
  onHandleSubmit,
  rangeType,
  setRangeType,
}) => {
  const currentYear = new DateObject({ calendar: persian }).year; // Get the current year in the Persian calendar
  const minDate = new Date("2024-01-21");
  const todayDate = new Date(); // the date of today
  const minYear = 1402; // The minimum year allowed for selection
  const [dates, setDates] = useState([selectedStartDate, selectedEndDate]);
  const [dailyDateValue, setDailyDateValue] = useState();
  const [weeklyDateValue, setWeeklyDateValue] = useState();
  const [customRange3DayStart, setCustomRange3DayStart] = useState();
  const [customRange3DayEnd, setCustomRange3DayEnd] = useState();
  const [customRange30DayStart, setCustomRange30DayStart] = useState();
  const [customRange30DayEnd, setCustomRange30DayEnd] = useState();
  const [customNoLimitRangeStart, setCustomNoLimitRangeStart] = useState();
  const [customNoLimitRangeEnd, setCustomNoLimitRangeEnd] = useState();
  const [monthlyDateValue, setMonthlyDateValue] = useState();
  const [yearlyDateValue, setYearlyDateValue] = useState(todayDate);

  const handleDataTypeChange = (e) => {
    const newDataType = e.target.value;

    // Reset inputs to "empty" whenever dataType changes
    setRangeType("empty");
    setSelectedStartDate(null);
    setSelectedEndDate(null);

    // Additional condition if the newDataType is "online" and rangeType is "monthly" or "yearly"
    if (
      newDataType === "online" &&
      (rangeType === "monthly" || rangeType === "yearly")
    ) {
      setRangeType("empty");
    }

    // Finally, update the dataType state
    setDataType(newDataType);
  };

  useEffect(() => {
    if (rangeType === "daily" && dailyDateValue) {
      const dates = formatChartDateDaily(dailyDateValue, dataType);
      setSelectedStartDate(dates.startDate);
      setSelectedEndDate(dates.endDate);
    } else if (rangeType === "weekly" && weeklyDateValue) {
      const dates = formatChartDateWeekly(weeklyDateValue, dataType);
      setSelectedStartDate(dates.startDate);
      setSelectedEndDate(dates.endDate);
    } else if (
      rangeType === "custom3DaysLimit" &&
      customRange3DayStart &&
      customRange3DayEnd
    ) {
      const dates = formatChartDateCustom(
        customRange3DayStart,
        customRange3DayEnd,
        dataType
      );
      setSelectedStartDate(dates.startDate);
      setSelectedEndDate(dates.endDate);
    } else if (
      rangeType === "custom30DaysLimit" && // Typo fixed here
      customRange30DayStart &&
      customRange30DayEnd
    ) {
      const dates = formatChartDateCustom(
        customRange30DayStart,
        customRange30DayEnd,
        dataType
      );
      setSelectedStartDate(dates.startDate);
      setSelectedEndDate(dates.endDate);
    } else if (
      rangeType === "customNoLimit" && // Typo fixed here
      customNoLimitRangeStart &&
      customNoLimitRangeEnd &&
      dataType
    ) {
      const dates = formatChartDateCustom(
        customNoLimitRangeStart,
        customNoLimitRangeEnd,
        dataType
      );
      setSelectedStartDate(dates.startDate);
      setSelectedEndDate(dates.endDate);
    } else if (rangeType === "monthly" && monthlyDateValue) {
      const dates = formatChartDateMonthly(monthlyDateValue, dataType);
      setSelectedStartDate(dates.startDate);
      setSelectedEndDate(dates.endDate);
    } else if (rangeType === "yearly" && yearlyDateValue) {
      const dates = formatChartDateYearly(yearlyDateValue);
      setSelectedStartDate(dates.startDate);
      setSelectedEndDate(dates.endDate);
    }
  }, [
    dailyDateValue,
    weeklyDateValue,
    customRange3DayStart,
    customRange3DayEnd,
    customRange30DayStart,
    customRange30DayEnd,
    customNoLimitRangeStart,
    customNoLimitRangeEnd,
    monthlyDateValue,
    yearlyDateValue,
    rangeType,
    dataType,
  ]);

  const minInitialStart = new Date("2024/01/21");
  const today = new Date();
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth() + 1; // Months are zero-indexed
  const day = today.getUTCDate();
  const hours = today.getUTCHours();
  const minutes = today.getUTCMinutes();
  const seconds = today.getUTCSeconds();
  const todayUTC = `UTC Date: ${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const lastMonth = new Date(today);
  lastMonth.setMonth(today.getMonth() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);
  const [min3DaysStartDate, setMin3DaysStartDate] = useState(minInitialStart);
  const [max3DaysStartDate, setMax3DaysStartDate] = useState(yesterday);
  const [min3DaysEndDate, setMin3DaysEndDate] = useState(null);
  const [max3DaysEndDate, setMax3DaysEndDate] = useState(todayUTC);
  const [min30DaysStartDate, setMin15DaysStartDate] = useState(minInitialStart);
  const [max30DaysStartDate, setMax15DaysStartDate] = useState(yesterday);
  const [min30DaysEndDate, setMin15DaysEndDate] = useState(null);
  const [max30DaysEndDate, setMax15DaysEndDate] = useState(todayUTC);
  const [minNoLimitStartDate, setMinNoLimitStartDate] =
    useState(minInitialStart);
  const [maxNoLimitStartDate, setMaxNoLimitStartDate] = useState(yesterday);
  const [minNoLimitEndDate, setMinNoLimitEndDate] = useState(minInitialStart);
  const [maxNoLimitEndDate, setMaxNoLimitEndDate] = useState(yesterday);

  const handleCustomRangeStart3DayChange = (start) => {
    // Convert start to Gregorian for calculation
    const startDateInput = new DateObject({
      date: start,
      calendar: persian,
    }).toDate();

    // Calculate minEndDate and maxEndDate in Gregorian calendar
    // Calculate minEndDate and maxEndDate in Gregorian calendar
    const minEnd = new Date(startDateInput);
    minEnd.setDate(startDateInput.getDate() + 1); // The day after startDateInput

    const maxEnd = new Date(startDateInput);
    maxEnd.setDate(startDateInput.getDate() + 3); // 3 days after startDateInput

    // Ensure end dates don't exceed today
    const finalMinEndDate = minEnd > today ? today : minEnd;
    const finalMaxEndDate = maxEnd > today ? today : maxEnd;

    // Convert the results back to Persian dates
    const persianMinEndDate = new DateObject({
      date: finalMinEndDate,
      calendar: persian,
    });
    const persianMaxEndDate = new DateObject({
      date: finalMaxEndDate,
      calendar: persian,
    });

    // Set the state values with Persian dates
    setMin3DaysEndDate(persianMinEndDate);
    setMax3DaysEndDate(persianMaxEndDate);
  };

  const handleCustomRange30DayStartChange = (start) => {
    const fifteenDaysLimitStartInput = new DateObject({
      date: start,
      calendar: persian,
    }).toDate();

    // console.log("fifteenDaysLimitStartInput : ", fifteenDaysLimitStartInput);

    // Calculate minEndDate in Gregorian calendar
    const minEnd = new Date(fifteenDaysLimitStartInput);
    minEnd.setDate(fifteenDaysLimitStartInput.getDate() + 1); // The day after fifteenDaysLimitStartInput
    const maxEnd = new Date(fifteenDaysLimitStartInput);
    maxEnd.setDate(fifteenDaysLimitStartInput.getDate() + 30); // 3 days after fifteenDaysLimitStartInput

    // Ensure end dates don't exceed today
    const finalMinEndDate = minEnd > today ? today : minEnd;
    const finalMaxEndDate = maxEnd > today ? today : maxEnd;

    // Convert the results back to Persian dates
    const persianMinEndDate = new DateObject({
      date: finalMinEndDate,
      calendar: persian,
    });
    const persianMaxEndDate = new DateObject({
      date: finalMaxEndDate,
      calendar: persian,
    });

    // Set the state values with Persian dates
    setMin15DaysEndDate(persianMinEndDate);
    setMax15DaysEndDate(persianMaxEndDate);
  };

  const handleCustomRangeNoLimitStartChange = (start) => {
    const noLimitStartInput = new DateObject({
      date: start,
      calendar: persian,
    }).toDate();

    // console.log("noLimitStartInput : ", noLimitStartInput);

    // Calculate minEndDate in Gregorian calendar
    const minEnd = new Date(noLimitStartInput);
    minEnd.setDate(noLimitStartInput.getDate() + 1); // The day after noLimitStartInput
    const maxEnd = new Date(noLimitStartInput);
    maxEnd.setDate(noLimitStartInput.getDate() + 364); // 364 days after noLimitStartInput

    // Ensure end dates don't exceed today
    const finalMinEndDate = minEnd > today ? today : minEnd;
    const finalMaxEndDate = maxEnd > today ? today : maxEnd;

    // Convert the results back to Persian dates
    const persianMinEndDate = new DateObject({
      date: finalMinEndDate,
      calendar: persian,
    });
    const persianMaxEndDate = new DateObject({
      date: finalMaxEndDate,
      calendar: persian,
    });

    // Set the state values with Persian dates
    setMinNoLimitEndDate(persianMinEndDate);
    setMaxNoLimitEndDate(persianMaxEndDate);
  };

  useEffect(() => {
    if (customRange3DayStart) {
      handleCustomRangeStart3DayChange(customRange3DayStart);
      setCustomRange3DayEnd(null);
    } else if (customRange30DayStart) {
      handleCustomRange30DayStartChange(customRange30DayStart);
      setCustomRange30DayEnd(null);
    } else if (customNoLimitRangeStart) {
      handleCustomRangeNoLimitStartChange(customNoLimitRangeStart);
      setCustomNoLimitRangeEnd(null);
    }
  }, [
    rangeType,
    customRange3DayStart,
    customRange30DayStart,
    customNoLimitRangeStart,
  ]);

  const handleParameterChange = (event) => {
    setParameter(event.target.value);
  };

  return (
    <div className="col-lg-4 col-xl-3 my-5">
      <form onSubmit={onHandleSubmit}>
        <div className="bg-secondary p-2">
          {/* Data Type Selection */}
          <div
            className="px-2 d-flex justify-content-between align-items-center my-5"
            style={{ width: "100%" }}
          >
            <label className="text-light" htmlFor="data-type">
              انتخاب نوع داده
            </label>
            <select
              name="data-type"
              id="data-type"
              onChange={handleDataTypeChange}
              value={dataType}
              required
            >
              <option value="empty">...</option>
              <option value="online">شاخص لحظه ای</option>
              <option value="hourly">شاخص ساعتی</option>
              <option value="daily">شاخص روزانه</option>
              <option value="twelve">شاخص هم اکنون</option>
              <option value="twentyFour">شاخص آنلاین</option>
            </select>
          </div>

          {/* Range Type Selection */}
          <div
            className="px-2 d-flex justify-content-between align-items-center my-5"
            style={{ width: "100%" }}
          >
            <label className="text-light" htmlFor="range-type">
              انتخاب بازه
            </label>
            <select
              className="text-small"
              name="range-type"
              id="range-type"
              value={rangeType}
              onChange={(e) => setRangeType(e.target.value)}
              required
            >
              <option value="empty">...</option>
              {dataType !== "daily" && (
                <option value="daily">نمودار روزانه</option>
              )}
              {dataType === "hourly" ||
              dataType === "twelve" ||
              dataType === "twentyFour" ? (
                <option value="weekly">نمودار هفتگی</option>
              ) : (
                ""
              )}
              {dataType === "online" ? (
                <option value="custom3DaysLimit">
                  انتخاب بازه دلخواه (حداکثر 3 روز)
                </option>
              ) : dataType === "hourly" ||
                dataType === "twelve" ||
                dataType === "twentyFour" ? (
                <option value="custom30DaysLimit">
                  انتخاب بازه دلخواه (حداکثر 30 روز)
                </option>
              ) : dataType === "daily" ? (
                <option value="customNoLimit">انتخاب بازه دلخواه</option>
              ) : (
                ""
              )}
              {dataType !== "online" ? (
                <>
                  <option value="monthly">نمودار ماهانه</option>
                </>
              ) : null}
              {dataType === "daily" ? (
                <option value="yearly">نمودار سالانه</option>
              ) : (
                ""
              )}
            </select>
          </div>

          {/* selectedStartDate & selectedEndDate Selection */}
          {rangeType === "daily" ? (
            <div
              className="px-2 d-flex justify-content-between align-items-center my-5"
              style={{ width: "100%" }}
            >
              <label className="text-light" htmlFor="daily-date">
                انتخاب روز
              </label>
              <DatePicker
                single
                value={dailyDateValue}
                onChange={setDailyDateValue}
                minDate={minDate}
                maxDate={today}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                hideOnScroll
                render={
                  <DataPickerButton
                    className="text-light bg-dark rounded-1"
                    placeholder="انتخاب"
                  />
                }
                format="YYYY/MM/DD"
                required
              />
            </div>
          ) : null}

          {rangeType === "weekly" ? (
            <div
              className="px-2 d-flex justify-content-between align-items-center my-5"
              style={{ width: "100%" }}
            >
              <label className="text-light" htmlFor="weekly-date">
                انتخاب هفته
              </label>
              <DatePicker
                range
                weekPicker
                value={weeklyDateValue}
                minDate={minDate}
                maxDate={today}
                onChange={setWeeklyDateValue}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                hideOnScroll
                render={
                  <DataPickerButton
                    className="text-light bg-dark rounded-1"
                    placeholder="انتخاب"
                  />
                }
                format="YYYY/MM/DD"
                required
              />
            </div>
          ) : null}

          {rangeType === "custom3DaysLimit" ? (
            <>
              <div
                className="px-2 d-flex justify-content-between align-items-center my-5"
                style={{ width: "100%" }}
              >
                <label className="text-light" htmlFor="custom-range-start">
                  انتخاب شروع بازه
                </label>
                <DatePicker
                  single
                  value={customRange3DayStart}
                  onChange={setCustomRange3DayStart}
                  minDate={min3DaysStartDate}
                  maxDate={max3DaysStartDate}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  hideOnScroll
                  render={
                    <DataPickerButton
                      className="text-light bg-dark rounded-1"
                      placeholder="انتخاب"
                    />
                  }
                  format="YYYY/MM/DD"
                  required
                />
              </div>
              <div
                className="px-2 d-flex justify-content-between align-items-center my-5"
                style={{ width: "100%" }}
              >
                <label className="text-light" htmlFor="custom-range-end">
                  انتخاب پایان بازه
                </label>
                <DatePicker
                  single
                  value={customRange3DayEnd}
                  minDate={min3DaysEndDate}
                  maxDate={max3DaysEndDate}
                  onChange={setCustomRange3DayEnd}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  hideOnScroll
                  render={
                    <DataPickerButton
                      className="text-light bg-dark rounded-1"
                      placeholder="انتخاب"
                    />
                  }
                  format="YYYY/MM/DD"
                  required
                />
              </div>
            </>
          ) : null}

          {rangeType === "custom30DaysLimit" ? (
            <>
              <div
                className="px-2 d-flex justify-content-between align-items-center my-5"
                style={{ width: "100%" }}
              >
                <label className="text-light" htmlFor="custom-range-start">
                  انتخاب شروع بازه
                </label>
                <DatePicker
                  single
                  value={customRange30DayStart}
                  onChange={setCustomRange30DayStart}
                  minDate={min30DaysStartDate}
                  maxDate={max30DaysStartDate}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  hideOnScroll
                  render={
                    <DataPickerButton
                      className="text-light bg-dark rounded-1"
                      placeholder="انتخاب"
                    />
                  }
                  format="YYYY/MM/DD"
                  required
                />
              </div>
              <div
                className="px-2 d-flex justify-content-between align-items-center my-5"
                style={{ width: "100%" }}
              >
                <label className="text-light" htmlFor="custom-range-end">
                  انتخاب پایان بازه
                </label>
                <DatePicker
                  single
                  value={customRange30DayEnd}
                  minDate={min30DaysEndDate}
                  maxDate={max30DaysEndDate}
                  onChange={setCustomRange30DayEnd}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  hideOnScroll
                  render={
                    <DataPickerButton
                      className="text-light bg-dark rounded-1"
                      placeholder="انتخاب"
                    />
                  }
                  format="YYYY/MM/DD"
                  required
                />
              </div>
            </>
          ) : null}

          {rangeType === "customNoLimit" ? (
            <>
              <div
                className="px-2 d-flex justify-content-between align-items-center my-5"
                style={{ width: "100%" }}
              >
                <label
                  className="text-light"
                  htmlFor="custom-no-limit-range-start"
                >
                  انتخاب شروع بازه
                </label>
                <DatePicker
                  single
                  value={customNoLimitRangeStart}
                  onChange={setCustomNoLimitRangeStart}
                  minDate={minNoLimitStartDate}
                  maxDate={maxNoLimitStartDate}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  hideOnScroll
                  render={
                    <DataPickerButton
                      className="text-light bg-dark rounded-1"
                      placeholder="انتخاب"
                    />
                  }
                  format="YYYY/MM/DD"
                  required
                />
              </div>
              <div
                className="px-2 d-flex justify-content-between align-items-center my-5"
                style={{ width: "100%" }}
              >
                <label
                  className="text-light"
                  htmlFor="custom-no-limit-range-end"
                >
                  انتخاب پایان بازه
                </label>
                <DatePicker
                  single
                  value={customNoLimitRangeEnd}
                  onChange={setCustomNoLimitRangeEnd}
                  minDate={minNoLimitEndDate}
                  maxDate={maxNoLimitEndDate}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  hideOnScroll
                  render={
                    <DataPickerButton
                      className="text-light bg-dark rounded-1"
                      placeholder="انتخاب"
                    />
                  }
                  format="YYYY/MM/DD"
                  required
                />
              </div>
            </>
          ) : null}

          {rangeType === "monthly" ? (
            <div
              className="px-2 d-flex justify-content-between align-items-center my-5"
              style={{ width: "100%" }}
            >
              <label className="text-light" htmlFor="monthly-date">
                انتخاب ماه
              </label>
              <DatePicker
                single
                onlyMonthPicker
                value={monthlyDateValue}
                onChange={setMonthlyDateValue}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                hideOnScroll
                minDate={minDate}
                maxDate={lastMonth}
                render={
                  <DataPickerButton
                    className="text-light bg-dark rounded-1"
                    placeholder="انتخاب"
                  />
                }
                format="YYYY/MM"
                required
              />
            </div>
          ) : null}

          {rangeType === "yearly" ? (
            <div
              className="px-2 d-flex justify-content-between align-items-center my-5"
              style={{ width: "100%" }}
            >
              <label className="text-light" htmlFor="yearly-date">
                انتخاب سال
              </label>
              <DatePicker
                single
                onlyYearPicker
                value={yearlyDateValue}
                onChange={setYearlyDateValue}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                hideOnScroll
                minDate={new DateObject({ year: minYear, calendar: persian })}
                maxDate={
                  new DateObject({ year: currentYear, calendar: persian })
                }
                render={
                  <DataPickerButton
                    className="text-light bg-dark rounded-1"
                    placeholder="انتخاب"
                  />
                }
                format="YYYY"
                required
              />
            </div>
          ) : null}

          <div
            className="px-2 d-flex justify-content-between align-items-center my-5"
            style={{ width: "100%" }}
          >
            <label className="text-light" htmlFor="chart-type">
              انتخاب نوع نمودار
            </label>
            <select
              name="chart-type"
              id="chart-type"
              onChange={(e) => setChartType(e.target.value)}
              required
            >
              <option value="empty">...</option>
              <option value="bar">نمودار میله ای</option>
              <option value="scatter">نمودار پراکندگی</option>
            </select>
          </div>

          <div
            className="px-2 d-flex justify-content-between align-items-center my-5"
            style={{ width: "100%" }}
          >
            <label className="text-light" htmlFor="parameter">
              انتخاب پارامتر
            </label>
            <select
              name="parameter"
              id="parameter"
              className="text-small"
              onChange={handleParameterChange}
              required
            >
              <option value="empty">...</option>
              {device === "SAPA" ||
              device === "OSPM" ||
              device === "ESPS" ||
              device === "SATA" ? (
                <>
                  <option value="aqi">شاخص کیفیت هوا (AQI)</option>
                  <option value="pm25">ذرات معلق 2.5 میکرون (PM2.5)</option>
                  <option value="pm10">ذرات معلق 10 میکرون (PM10)</option>
                </>
              ) : null}
              {device === "SAPA" || device === "SATA" ? (
                <>
                  <option value="co">گاز کربن منواکسید (CO)</option>
                  <option value="o3">گاز ازن (O3)</option>
                  <option value="no2">گاز نیتروژن دی اکسید (NO2)</option>
                  <option value="so2">گاز گوگرد دی اکسید(SO2)</option>
                </>
              ) : null}

              {/* {dataType === "online" ? (
                <>
                  {device === "SAPA" ||
                  device === "OSPM" ||
                  device === "ESPS" ||
                  device === "SATA" ? (
                    <>
                      <option value="temperature">دما (Temperature)</option>
                      <option value="humidity">رطوبت (Humidity)</option>
                    </>
                  ) : null}
                  {device === "ESPS" ? (
                    <option value="pressure">فشار (Pressure)</option>
                  ) : null}
                </>
              ) : null} */}
            </select>
          </div>

          <div className="mx-auto my-5 d-flex justify-content-center">
            <button type="submit" className="btn btn-primary">
              رسم نمودار
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChartsDateInputs;
